import { useEffect, useState } from 'react';
import AFramePlayer from './components/player/AFramePlayer';
import GridTest from './GridTest';

// Simple route check
const isGridTest = window.location.pathname === '/grid-test';

interface Clip {
  id: string;
  title: string;
  duration_seconds: number;
}

// Use relative URLs in development (Vite proxy), full URL in production
const API_BASE = import.meta.env.DEV
  ? ''
  : 'https://jordan-360-clip-lab-api.sheely121.workers.dev';

function App() {
  const [, setClip] = useState<Clip | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [startTime, setStartTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip loading if on grid test page
    if (isGridTest) return;
    async function loadRandomClip() {
      try {
        // Fetch all clips from library
        const response = await fetch(`${API_BASE}/api/library`);
        if (!response.ok) throw new Error('Failed to fetch library');

        const data = await response.json();
        const clips = data.clips || [];

        if (clips.length === 0) {
          throw new Error('No clips available');
        }

        // Pick a random clip
        const randomClip = clips[Math.floor(Math.random() * clips.length)];
        setClip(randomClip);

        // Set random start time (0 to 75% of duration to ensure some playback)
        const maxStart = randomClip.duration_seconds * 0.75;
        const randomStart = Math.random() * maxStart;
        setStartTime(randomStart);

        // Try HLS first (adaptive streaming), fall back to direct stream
        const hlsUrl = `${API_BASE}/hls/${randomClip.id}/master.m3u8`;
        const directUrl = `${API_BASE}/api/clip/${randomClip.id}/stream`;

        // Check if HLS exists
        try {
          const hlsCheck = await fetch(hlsUrl, { method: 'HEAD' });
          if (hlsCheck.ok) {
            console.log('Using HLS adaptive streaming');
            setVideoUrl(hlsUrl);
          } else {
            console.log('HLS not available, using direct stream');
            setVideoUrl(directUrl);
          }
        } catch {
          console.log('HLS check failed, using direct stream');
          setVideoUrl(directUrl);
        }
      } catch (err) {
        console.error('Failed to load clip:', err);
        setError(err instanceof Error ? err.message : 'Failed to load video');
      } finally {
        setLoading(false);
      }
    }

    loadRandomClip();
  }, []);

  // Show grid test page if on /grid-test route
  if (isGridTest) {
    return <GridTest />;
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">Error: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-white/20 rounded-lg hover:bg-white/30"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!videoUrl) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <p className="text-white text-xl">No video available</p>
      </div>
    );
  }

  return (
    <>
      <AFramePlayer
        videoUrl={videoUrl}
        startTime={startTime}
        autoPlay={true}
      />
      <TapToStart />
    </>
  );
}

function TapToStart() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      const v = document.getElementById('video-360') as HTMLVideoElement | null;
      if (!cancelled && v && v.paused) setShow(true);
    }, 1800);
    const dismiss = () => {
      const v = document.getElementById('video-360') as HTMLVideoElement | null;
      if (v && v.paused) v.play().catch(() => {});
      setShow(false);
    };
    window.addEventListener('pointerdown', dismiss, { once: true });
    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener('pointerdown', dismiss);
    };
  }, []);
  if (!show) return null;
  return (
    <div
      onClick={() => {
        const v = document.getElementById('video-360') as HTMLVideoElement | null;
        if (v) v.play().catch(() => {});
        setShow(false);
      }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 cursor-pointer"
    >
      <div className="text-center text-white">
        <div className="text-6xl mb-4">▶</div>
        <div className="text-xl font-medium">Tap to start</div>
        <div className="text-sm text-white/60 mt-2">360° paramotor footage</div>
      </div>
    </div>
  );
}

export default App;
